<script setup lang="ts">
import { computed, inject, useId, watch } from 'vue'

import { COLLAPSE_GROUP } from './collapse'

const props = withDefaults(defineProps<{ id?: string; disabled?: boolean }>(), { id: '', disabled: false })

/** Open state; inside an `AppCollapseGroup`, opening one closes the others. */
const open = defineModel<boolean>('open', { default: false })
const itemId = props.id || `collapse-${useId()}`
const group = inject(COLLAPSE_GROUP, null)

if (group) {
  if (open.value) group.openId.value = itemId
  watch(open, (value) => {
    if (value) group.openId.value = itemId
    else if (group.openId.value === itemId) group.openId.value = null
  })
  watch(group.openId, (id) => {
    if (id !== itemId && open.value) open.value = false
  })
}

const expanded = computed(() => open.value && !props.disabled)
function toggle(): void {
  if (!props.disabled) open.value = !open.value
}
</script>

<template>
  <div class="app-collapse" :class="{ 'is-open': expanded }" data-kit="collapse">
    <slot name="header" :open="expanded" :toggle="toggle" :controls="`${itemId}-content`" />
    <div :id="`${itemId}-content`" class="app-collapse__body" :class="{ 'is-open': expanded }" :aria-hidden="!expanded">
      <div class="app-collapse__inner">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-collapse__body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}
.app-collapse__body.is-open {
  grid-template-rows: 1fr;
}
.app-collapse__inner {
  overflow: hidden;
}
</style>
